// The organizer register ("Klubber og arrangører"): Firestore and Storage
// access for the clubs, schools and federations that run tournaments.
//
// SHAPE (firestore.rules enforces every transition below)
//   organizers/{orgId}        public profile: name, shortName, type, city,
//                             federation, email, phone, website, logoUrl,
//                             nameKey, verified, createdBy, createdAt, updatedAt
//   organizerRosters/{orgId}  private: orgId, ownerId, admins[], arbiters[],
//                             pendingAdmins[], pendingArbiters[], names{email: name}
//   tournaments/{id}          organizerId + organizerName (a copy, so public
//                             lists need no extra read and an old tournament
//                             keeps the name it was run under)
//
// Membership is the same model as sharing a player database (player-db.js):
// an admin invites a registered arbiter by email, the address goes into a
// pending list and an `org_invite` notification is sent; the invitee accepts or
// declines. The list change always comes first and the notification clean-up
// after, so a failure never destroys the way back into the flow. The checks
// here exist for a clear message; the rules are the security.
//
// NAMING. Not to be confused with the account role claim `organizer`, which is
// the old internal name for an arbiter account.

import {
    collection, doc, getDoc, getDocs, updateDoc, setDoc, deleteDoc,
    query, where, limit, writeBatch, arrayUnion, arrayRemove, deleteField, FieldPath
} from "https://www.gstatic.com/firebasejs/12.12.1/firebase-firestore.js";
// Straight from the modules that own them, not through firebase-auth.js:
// "Delete my profile" in firebase-auth.js calls into this file, and that file's
// imports run one way only.
import { db, auth, storage, storageRef, uploadBytes, getDownloadURL, deleteObject } from "./firebase-core.js";
import { lookupArbiterByEmail } from "./arbiters.js";
import { prepareImageForUpload } from "./media.js";
import { IMAGE_PRESETS } from "../utils/image-resize.js";
import { offlineError } from "../utils/offline-guards.js";
import {
    normEmail, roleIn, isMemberRole, wouldLeaveNoAdmin, inviteProblem, validateOrganizerForm,
    accountExitPlan, hasOtherMembers
} from "../utils/organizer-logic.js";

const ORGS = 'organizers';
const ROSTERS = 'organizerRosters';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function requireUser() {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    return user;
}

function myEmail() {
    const user = auth.currentUser;
    return user ? normEmail(user.email) : '';
}

function myName(user) {
    return (user.displayName || '').trim() || (user.email || '').split('@')[0];
}

function newNotificationId() {
    return Date.now().toString() + '_' + Math.random().toString(36).substring(2, 8);
}

function notify(recipientEmail, type, org, extra = {}) {
    const user = requireUser();
    return setDoc(doc(db, 'notifications', newNotificationId()), {
        recipientEmail: normEmail(recipientEmail),
        senderUid: user.uid,
        senderEmail: user.email,
        senderName: myName(user),
        organizerId: String(org.id),
        organizerName: org.name || '',
        type,
        status: 'pending',
        timestamp: new Date().toISOString(),
        ...extra
    });
}

// Outstanding org_invite notifications for this organizer, sent to these
// addresses. Returns what it found (the invitee's answer needs to know who sent
// the invitation) and deletes them. Best-effort: the roster is the authority,
// and a leftover notification only offers an Accept the rules will refuse.
async function takeInviteNotifications(orgId, emails) {
    const found = [];
    await Promise.all((emails || []).map(normEmail).filter(Boolean).map(async (email) => {
        try {
            const snap = await getDocs(query(
                collection(db, 'notifications'),
                where('recipientEmail', '==', email),
                where('organizerId', '==', String(orgId)),
                where('type', '==', 'org_invite')
            ));
            snap.docs.forEach(d => found.push({ id: d.id, ...d.data() }));
            await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
        } catch (e) {
            console.warn('Could not clear an organizer invitation notification:', e);
        }
    }));
    return found;
}

// Short-lived cache of the whole public register: it backs the "is it already
// registered?" search, which runs on every keystroke. Small by nature — one
// document per club in the country.
const ALL_TTL_MS = 60 * 1000;
let allCache = null;

function forgetAll() {
    allCache = null;
}

// "My organizers" is asked for by the page, the tournament form and the
// notification handler, often within the same second. One read serves them.
const MINE_TTL_MS = 30 * 1000;
let mineCache = null;

export function forgetMyOrganizers() {
    mineCache = null;
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

/** Every registered organizer's public profile. */
export async function listAllOrganizers() {
    if (allCache && Date.now() - allCache.at < ALL_TTL_MS) return allCache.promise;
    const promise = getDocs(query(collection(db, ORGS), limit(2000)))
        .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
    allCache = { at: Date.now(), promise };
    promise.catch(() => { allCache = null; });
    return promise;
}

export async function getOrganizer(orgId) {
    if (!orgId) return null;
    const snap = await getDoc(doc(db, ORGS, String(orgId)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getRoster(orgId) {
    const snap = await getDoc(doc(db, ROSTERS, String(orgId)));
    return snap.exists() ? snap.data() : null;
}

/**
 * Every roster that names this address, as { id, roster, profile }.
 *
 * Four queries rather than one because Firestore cannot OR across fields, and
 * the rules can only prove a query against the list it names.
 *
 * `strict` is for decisions that must not be made on a partial answer
 * (deleting an account): a failed query, or one answered from the offline
 * cache, throws instead of being treated as "not on that list". It also keeps
 * a roster whose profile is gone (a club deleted halfway), because the address
 * on it still has to come off.
 */
async function readMyRosters(email, { strict = false } = {}) {
    const col = collection(db, ROSTERS);
    const lists = ['admins', 'arbiters', 'pendingAdmins', 'pendingArbiters'];
    const snaps = await Promise.all(lists.map(list => {
        const read = getDocs(query(col, where(list, 'array-contains', email)));
        return strict ? read : read.catch(() => null);
    }));
    if (strict && snaps.some(snap => snap.metadata.fromCache)) {
        throw offlineError('Could not reach the server to check your clubs and organizers.');
    }
    const rosters = new Map();
    snaps.forEach(snap => {
        if (snap) snap.docs.forEach(d => { if (!rosters.has(d.id)) rosters.set(d.id, d.data()); });
    });

    const items = await Promise.all(Array.from(rosters, async ([id, roster]) => {
        const read = getOrganizer(id);
        const profile = strict ? await read : await read.catch(() => null);
        if (!profile && !strict) return null; // a roster whose club was deleted halfway
        return { id, roster, profile };
    }));
    return items.filter(Boolean);
}

/**
 * Every organizer this arbiter belongs to or has been invited to, each as its
 * public profile plus `roster` and `myRole` ('admin' | 'arbiter' |
 * 'pendingAdmin' | 'pendingArbiter').
 */
export async function listMyOrganizers({ fresh = false } = {}) {
    const user = auth.currentUser;
    const email = myEmail();
    if (!user || !email) return [];
    if (!fresh && mineCache && mineCache.uid === user.uid && Date.now() - mineCache.at < MINE_TTL_MS) {
        return mineCache.promise;
    }

    const promise = readMyRosters(email).then(items => items.map(({ roster, profile }) =>
        ({ ...profile, roster, myRole: roleIn(roster, email) })));

    mineCache = { uid: user.uid, at: Date.now(), promise };
    promise.catch(() => { mineCache = null; });
    return promise;
}

/**
 * Public tournaments that name this organizer, from the spectators' mirror
 * (publicTournaments, written by the mirrorPublicTournament function), which holds
 * public tournaments only — private ones are their arbiters' business, and the
 * tournament documents themselves are readable by their own arbiters alone.
 */
export async function listOrganizerPublicTournaments(orgId) {
    const snap = await getDocs(query(
        collection(db, 'publicTournaments'),
        where('organizerId', '==', String(orgId)),
        limit(200)
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ---------------------------------------------------------------------------
// Registering and editing
// ---------------------------------------------------------------------------

/**
 * Register a new organizer, with the caller as its only admin. The profile and
 * the roster are written in one batch: the rules accept neither without the
 * other.
 */
export async function registerOrganizer(values) {
    const user = requireUser();
    const email = myEmail();
    const { ok, errors, profile } = validateOrganizerForm(values);
    if (!ok) throw new Error(errors[0]);

    const ref = doc(collection(db, ORGS));
    const now = new Date().toISOString();
    const batch = writeBatch(db);
    batch.set(ref, {
        ...profile,
        verified: false,
        createdBy: user.uid,
        createdAt: now,
        updatedAt: now
    });
    batch.set(doc(db, ROSTERS, ref.id), {
        orgId: ref.id,
        ownerId: user.uid,
        admins: [email],
        arbiters: [],
        pendingAdmins: [],
        pendingArbiters: [],
        names: { [email]: myName(user) }
    });
    await batch.commit();
    forgetAll();
    forgetMyOrganizers();
    return ref.id;
}

/** Change the public profile. Admins only. */
export async function updateOrganizerProfile(orgId, values) {
    requireUser();
    const { ok, errors, profile } = validateOrganizerForm(values);
    if (!ok) throw new Error(errors[0]);
    await updateDoc(doc(db, ORGS, String(orgId)), { ...profile, updatedAt: new Date().toISOString() });
    forgetAll();
    forgetMyOrganizers();
}

/**
 * Upload a logo and return its URL. Filed under the uploader's own folder (see
 * storage.rules); which picture the club shows is its profile's logoUrl.
 */
export async function uploadOrganizerLogo(file) {
    const user = requireUser();
    const image = await prepareImageForUpload(file, IMAGE_PRESETS.avatar);
    const safeName = String(image.name || 'logo').replace(/[^a-zA-Z0-9._-]/g, '_');
    const sRef = storageRef(storage, `organizer-logos/${user.uid}/${Date.now()}-${safeName}`);
    await uploadBytes(sRef, image, { contentType: image.type || 'image/png' });
    return getDownloadURL(sRef);
}

/**
 * Delete an organizer. Admins only. Every member and invitee is told, and open
 * invitations are withdrawn first, while the roster still exists — the rule
 * that lets an admin clear them resolves admin-ness through it. Tournaments
 * that named the club keep their copy of its name.
 */
export async function deleteOrganizer(orgId) {
    requireUser();
    const id = String(orgId);
    const [org, roster] = await Promise.all([getOrganizer(id), getRoster(id)]);
    if (!org || !roster) throw new Error('That organizer no longer exists.');
    if (roleIn(roster, myEmail()) !== 'admin') throw new Error('Only an admin of the organizer can delete it.');

    const me = myEmail();
    const pending = [...(roster.pendingAdmins || []), ...(roster.pendingArbiters || [])];
    const members = [...(roster.admins || []), ...(roster.arbiters || [])].map(normEmail).filter(e => e && e !== me);
    try {
        await takeInviteNotifications(id, pending);
        await Promise.all(members.map(email => notify(email, 'org_deleted', org)));
    } catch (e) {
        console.warn('Deleting an organizer: could not tell everyone:', e);
    }

    const batch = writeBatch(db);
    batch.delete(doc(db, ORGS, id));
    batch.delete(doc(db, ROSTERS, id));
    await batch.commit();
    forgetAll();
    forgetMyOrganizers();
    await deleteOwnLogo(org.logoUrl);
}

/**
 * Remove a deleted club's logo from Storage, if this account uploaded it.
 * Only the uploader's own folder can be touched (storage.rules), and a logo
 * another admin uploaded simply stays. Best-effort: the club is already gone.
 */
async function deleteOwnLogo(url) {
    const user = auth.currentUser;
    if (!user || !url || !url.includes(encodeURIComponent(`organizer-logos/${user.uid}/`))) return;
    try {
        await deleteObject(storageRef(storage, url));
    } catch (e) {
        console.warn('Could not delete the organizer logo:', e);
    }
}

// ---------------------------------------------------------------------------
// Deleting an account
//
// "Delete my profile" (js/services/firebase-auth.js) comes through here before
// it deletes anything else, so that the person's address and name come off
// every club's member list, and no club is left with nobody able to manage it.
// ---------------------------------------------------------------------------

/**
 * What deleting this account would do to its clubs (accountExitPlan in
 * js/utils/organizer-logic.js). Reads strictly: a decision that ends in
 * deleting clubs is not made on a partial answer, so this throws when the
 * server cannot be asked.
 */
export async function planAccountExit() {
    requireUser();
    const email = myEmail();
    const items = (await readMyRosters(email, { strict: true })).map(({ id, roster, profile }) =>
        ({ id, name: (profile && profile.name) || '', profile, roster }));
    return accountExitPlan(items, email);
}

/** An error that says which club a step failed on, for the message. */
function exitError(item, cause) {
    const err = new Error(`Could not take the account off organizer ${item.id}: ${cause && cause.message}`);
    err.code = 'organizer-exit-failed';
    err.organizerName = item.name || '';
    err.cause = cause;
    return err;
}

/**
 * Carry out a plan from planAccountExit: decline the open invitations (no reply
 * is sent), leave the clubs others run, and delete the ones this account is the
 * only member of. Stops at the first step that fails, before the account itself
 * is touched, so "Delete my profile" can be tried again; every step is safe to
 * repeat.
 *
 * A club about to be deleted is read again first. Someone may have accepted an
 * invitation since the plan was made, and then the club is no longer this
 * account's alone to delete.
 */
export async function carryOutAccountExit(plan) {
    requireUser();
    const me = myEmail();

    for (const item of plan.invitations) {
        try {
            await updateDoc(doc(db, ROSTERS, item.id), {
                pendingAdmins: arrayRemove(me),
                pendingArbiters: arrayRemove(me)
            });
            await takeInviteNotifications(item.id, [me]);
        } catch (e) {
            throw exitError(item, e);
        }
    }

    for (const item of plan.leaves) {
        try {
            // Both lists: whichever one holds the address loses it, and the
            // other is unchanged, which the rules do not count as touched.
            await updateDoc(doc(db, ROSTERS, item.id),
                'admins', arrayRemove(me),
                'arbiters', arrayRemove(me),
                new FieldPath('names', me), deleteField());
        } catch (e) {
            throw exitError(item, e);
        }
    }

    for (const item of plan.deletes) {
        try {
            const roster = await getRoster(item.id);
            if (!roster) continue; // already gone
            if (hasOtherMembers(roster, me)) {
                throw new Error('Someone else has joined since; this club is no longer yours alone to delete.');
            }
            if (item.profile) {
                await deleteOrganizer(item.id);
            } else {
                // The profile went in an earlier, interrupted delete; finish the job.
                await deleteDoc(doc(db, ROSTERS, item.id));
            }
        } catch (e) {
            throw exitError(item, e);
        }
    }

    forgetAll();
    forgetMyOrganizers();
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

async function readAsAdmin(orgId) {
    requireUser();
    const [org, roster] = await Promise.all([getOrganizer(orgId), getRoster(orgId)]);
    if (!org || !roster) throw new Error('That organizer no longer exists.');
    if (roleIn(roster, myEmail()) !== 'admin') throw new Error('Only an admin of the organizer can change who belongs to it.');
    return { org, roster };
}

/** Invite a registered arbiter, as 'admin' or 'arbiter'. Admins only. */
export async function inviteToOrganizer(orgId, email, role) {
    const target = normEmail(email);
    const { org, roster } = await readAsAdmin(orgId);
    const problem = inviteProblem(roster, target, myEmail());
    if (problem) throw new Error(problem);

    const lookup = await lookupArbiterByEmail(target);
    if (!lookup.exists) throw new Error('No registered account found with this email address.');
    if (lookup.role === 'player') {
        throw new Error('This email is registered with a Player account. Only arbiters can join an organizer.');
    }

    const list = role === 'admin' ? 'pendingAdmins' : 'pendingArbiters';
    await updateDoc(doc(db, ROSTERS, String(orgId)), { [list]: arrayUnion(target) });
    await notify(target, 'org_invite', org, { role: role === 'admin' ? 'admin' : 'arbiter' });
    forgetMyOrganizers();
    return target;
}

/** Withdraw an invitation nobody has answered yet. Admins only. */
export async function retractOrganizerInvite(orgId, email) {
    const target = normEmail(email);
    await readAsAdmin(orgId);
    await updateDoc(doc(db, ROSTERS, String(orgId)), {
        pendingAdmins: arrayRemove(target),
        pendingArbiters: arrayRemove(target)
    });
    await takeInviteNotifications(orgId, [target]);
    return target;
}

/** Take someone off the roster. Admins only; the last admin cannot go. */
export async function removeOrganizerMember(orgId, email) {
    const target = normEmail(email);
    const { org, roster } = await readAsAdmin(orgId);
    if (wouldLeaveNoAdmin(roster, target)) {
        throw new Error('An organizer needs at least one admin. Make someone else an admin first.');
    }
    await updateDoc(doc(db, ROSTERS, String(orgId)),
        'admins', arrayRemove(target),
        'arbiters', arrayRemove(target),
        new FieldPath('names', target), deleteField());
    if (target !== myEmail()) {
        try {
            await notify(target, 'org_member_removed', org);
        } catch (e) {
            console.warn('Could not tell the removed arbiter:', e);
        }
    }
    forgetMyOrganizers();
    return target;
}

/** Make a member an admin or an ordinary arbiter. Admins only. */
export async function setOrganizerRole(orgId, email, role) {
    const target = normEmail(email);
    const { roster } = await readAsAdmin(orgId);
    if (!isMemberRole(roleIn(roster, target))) throw new Error('That arbiter is not a member of this organizer.');
    if (role !== 'admin' && wouldLeaveNoAdmin(roster, target)) {
        throw new Error('An organizer needs at least one admin. Make someone else an admin first.');
    }
    const patch = role === 'admin'
        ? { arbiters: arrayRemove(target), admins: arrayUnion(target) }
        : { admins: arrayRemove(target), arbiters: arrayUnion(target) };
    await updateDoc(doc(db, ROSTERS, String(orgId)), patch);
    forgetMyOrganizers();
}

/**
 * Accept or decline an invitation, as the invitee. Writes only the lists the
 * rules allow an invitee to touch (plus their own name on accept), then clears
 * the invitation and tells whoever sent it.
 */
export async function answerOrganizerInvite(orgId, accept) {
    const user = requireUser();
    const me = myEmail();
    const id = String(orgId);
    // Read BEFORE answering: once declined, the invitee can no longer read the
    // roster, and the reply needs the club's name.
    const [org, roster] = await Promise.all([getOrganizer(id), getRoster(id)]);
    if (!org || !roster) throw new Error('That organizer no longer exists.');
    const role = roleIn(roster, me);
    if (role !== 'pendingAdmin' && role !== 'pendingArbiter') {
        throw new Error('This invitation is no longer open. It may have been withdrawn.');
    }

    const ref = doc(db, ROSTERS, id);
    if (accept) {
        const joined = role === 'pendingAdmin' ? 'admins' : 'arbiters';
        const pending = role === 'pendingAdmin' ? 'pendingAdmins' : 'pendingArbiters';
        await updateDoc(ref,
            pending, arrayRemove(me),
            joined, arrayUnion(me),
            new FieldPath('names', me), myName(user));
    } else {
        await updateDoc(ref, {
            pendingAdmins: arrayRemove(me),
            pendingArbiters: arrayRemove(me)
        });
    }
    forgetMyOrganizers();

    const invitations = await takeInviteNotifications(id, [me]);
    const inviters = [...new Set(invitations.map(n => normEmail(n.senderEmail)).filter(e => e && e !== me))];
    await Promise.all(inviters.map(email =>
        notify(email, accept ? 'org_invite_accepted' : 'org_invite_declined', org)
            .catch(e => console.warn('Could not tell the inviter about the answer:', e))));
    return org;
}

/** Leave an organizer. An admin may leave only while another admin remains. */
export async function leaveOrganizer(orgId) {
    requireUser();
    const me = myEmail();
    const roster = await getRoster(orgId);
    if (!roster) throw new Error('That organizer no longer exists.');
    const role = roleIn(roster, me);
    if (!isMemberRole(role)) throw new Error('You are not a member of this organizer.');
    if (wouldLeaveNoAdmin(roster, me)) {
        throw new Error('You are the only admin. Make someone else an admin before you leave, or delete the organizer.');
    }
    await updateDoc(doc(db, ROSTERS, String(orgId)),
        role === 'admin' ? 'admins' : 'arbiters', arrayRemove(me),
        new FieldPath('names', me), deleteField());
    forgetMyOrganizers();
}
